import { useCallback, useEffect, useState } from "react";

// Posiciona um elemento em portal a partir de um elemento âncora.
//
// Quem vai para portal na raiz deixa de herdar posicionamento do ancestral e passa a
// precisar de coordenadas próprias. É o preço de escapar do empilhamento do header, e é
// o que este hook resolve.
//
// Regras de docs/contexto-entrega/painel-entrega-documentacao_v2.md §3:
//   - alinhamento pela borda esquerda (gatilho da barra) ou direita (link "alterar")
//   - deslocamento vertical de 8px, direção preferida para baixo
//   - inverte para cima quando faltar espaço
//   - margem mínima de 16px das bordas da janela
//   - acompanha a rolagem e não fecha com ela

const OFFSET = 8;
const MARGEM = 16;

export default function useAnchoredPosition(anchorRef, panelRef, { align = "left" } = {}) {
  const [posicao, setPosicao] = useState(null);

  const reposicionar = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const a = anchor.getBoundingClientRect();
    const painel = panelRef.current;

    // Antes da primeira medição do painel, usa as medidas do documento de referência para
    // não piscar em uma posição errada no primeiro quadro.
    const largura = painel ? painel.offsetWidth : 380;
    const altura = painel ? painel.offsetHeight : 236;

    const espacoAbaixo = window.innerHeight - a.bottom - OFFSET - MARGEM;
    const paraCima = espacoAbaixo < altura && a.top - OFFSET - MARGEM > altura;

    const top = paraCima ? a.top - OFFSET - altura : a.bottom + OFFSET;

    let left = align === "right" ? a.right - largura : a.left;
    const maximo = window.innerWidth - MARGEM - largura;
    left = Math.max(MARGEM, Math.min(left, Math.max(MARGEM, maximo)));

    setPosicao({ top: Math.round(top), left: Math.round(left), paraCima });
  }, [anchorRef, panelRef, align]);

  useEffect(() => {
    reposicionar();

    // `true` na captura para acompanhar também a rolagem de contêineres internos, não só a
    // da janela. O painel acompanha a rolagem e não fecha com ela.
    window.addEventListener("scroll", reposicionar, true);
    window.addEventListener("resize", reposicionar);

    // O painel muda de altura em uso: a lista de endereços chega depois, a mensagem de erro
    // do CEP aparece e some. Sem observar o próprio elemento, a inversão para cima e a
    // margem da janela ficariam calculadas sobre uma altura velha.
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && panelRef.current) {
      observer = new ResizeObserver(reposicionar);
      observer.observe(panelRef.current);
    }

    return () => {
      window.removeEventListener("scroll", reposicionar, true);
      window.removeEventListener("resize", reposicionar);
      if (observer) observer.disconnect();
    };
  }, [reposicionar, panelRef]);

  return posicao;
}
